import React, { useEffect, useState } from "react";
import { X, Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import { estimate } from "../lib/billing";
import { useT } from "../lib/i18n";

export default function CreditEstimateDialog({ open, onClose, onConfirm, num_agents, rounds, output_format = "executive_summary", has_documents = false }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(false);
    estimate({ num_agents, rounds, output_format, has_documents })
      .then(setData)
      .catch(() => { setData(null); setError(true); })
      .finally(() => setLoading(false));
  }, [open, num_agents, rounds, output_format, has_documents, retryKey]);

  if (!open) return null;

  const insufficient = data && (!data.sufficient_credits || !data.plan_allowed);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-[#0B1B3D]/40 backdrop-blur-sm" data-testid="credit-estimate-dialog">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#0EA5E9]" />
            <p className="font-display font-extrabold text-[#0B1B3D]">{t("Confirm Konzenzo session")}</p>
          </div>
          <button data-testid="estimate-close" onClick={onClose} className="text-[#94A3B8] hover:text-[#0B1B3D]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {loading && <p className="text-sm text-[#94A3B8]">{t("Calculating cost…")}</p>}
          {!loading && error && (
            <div className="space-y-3" data-testid="estimate-error">
              <div className="rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 p-3 text-sm flex gap-2 text-[#0B1B3D]">
                <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" />
                <p>{t("Could not calculate the session cost. Check your connection and try again.")}</p>
              </div>
              <div className="flex justify-end">
                <button data-testid="estimate-retry" onClick={() => setRetryKey((k) => k + 1)} className="bg-[#0B1B3D] text-white hover:bg-[#13264F] px-5 py-2.5 rounded-lg font-bold text-sm font-display">{t("Retry")}</button>
              </div>
            </div>
          )}
          {data && (
            <>
              <div className="space-y-1">
                {data.estimate.breakdown.map((b) => (
                  <div key={b.label} className="flex items-center justify-between text-sm">
                    <span className="text-[#475569]">{b.label}</span>
                    <span className="font-mono-kz text-[#0B1B3D] font-bold">{b.cost} cr</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-2">
                  <span className="font-display font-extrabold text-[#0B1B3D]">{t("Total")}</span>
                  <span className="font-mono-kz text-[#0B1B3D] font-black text-lg" data-testid="estimate-total">{data.estimate.total} {t("credits")}</span>
                </div>
              </div>

              <div className="rounded-lg bg-[#F8FAFC] border border-slate-200 p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[#475569]">{t("Current balance")}</span>
                  <span className="font-mono-kz text-[#0B1B3D] font-bold">{data.current_balance} cr</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[#475569]">{t("After this run")}</span>
                  <span className={`font-mono-kz font-bold ${data.balance_after < 0 ? "text-[#EF4444]" : "text-[#10B981]"}`}>{data.balance_after} cr</span>
                </div>
              </div>

              {!data.plan_allowed && (
                <div className="rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-3 text-sm flex gap-2 text-[#0B1B3D]" data-testid="plan-block">
                  <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5 shrink-0" />
                  <p>{t("This session requires the")} <span className="font-bold">{data.plan_minimum_required}</span> {t("plan or higher.")}</p>
                </div>
              )}
              {!data.sufficient_credits && data.plan_allowed && (
                <div className="rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 p-3 text-sm flex gap-2 text-[#0B1B3D]" data-testid="balance-block">
                  <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" />
                  <p>{t("Insufficient credits. Buy a credit pack or upgrade your plan in the Billing page.")}</p>
                </div>
              )}

              <div className="flex items-start gap-2 text-xs text-[#475569]">
                <ShieldCheck className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
                <p>{t("Konzenzo provides AI-assisted decision support. Outputs may be inaccurate; validate before acting.")}</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button data-testid="estimate-cancel" onClick={onClose} className="text-sm font-bold text-[#475569] px-4 py-2.5 hover:text-[#0B1B3D]">{t("Cancel")}</button>
                <button
                  data-testid="estimate-confirm"
                  onClick={() => onConfirm?.(data)}
                  disabled={!!insufficient}
                  className="bg-[#10B981] disabled:opacity-40 text-white hover:bg-[#059669] px-5 py-2.5 rounded-lg font-bold text-sm font-display"
                >
                  {t("Run for")} {data.estimate.total} {t("credits")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
